/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import {
  CcfTypography,
  CcfAvatar,
  CcfBadge,
  CcfWorkingIcon,
  CcfAvailableIcon,
  CcfUnavailableIcon,
  CcfOfflineIcon,
  CcfMessageGroupIcon,
  CcfBox,
  CcfOverflowIcon,
} from '@nice-ccf/ui-controls';
import {
  CONVERSATION_CONTAINER_HEADER_HEIGHT,
  CONVERSATION_CONTAINER_REPLYBOX_HEIGHT,
  CONVERSATION_CONTAINER_FOOTER_HEIGHT,
} from '../utils/conversations.utils';
import {
  getSelectedAgent,
  getSelectedAgentType,
} from '../+state/ccf-conversations.slice';
import React from 'react';
import { useMsal } from '@azure/msal-react';

const useStyles = makeStyles((theme) =>
  createStyles({
    mainContainer: {
      height: `calc( 100% - ${CONVERSATION_CONTAINER_FOOTER_HEIGHT}px - ${CONVERSATION_CONTAINER_HEADER_HEIGHT}px -
        ${CONVERSATION_CONTAINER_REPLYBOX_HEIGHT}px )`,
      // height: '70%',
      overflow: 'auto',
    },
    listItemRoot: {
      alignItems: 'flex-start',
      justifyContent: 'center',
      gap: 10,
      paddingInline: 8,
      '&:hover': {
        backgroundColor: `var(--popover-hover-color)`,
      },
    },
    listItemIcon: {
      minWidth: 36,
      marginTop: 8,
    },
    listItemTextPrimary: {
      display: 'flex',
      flexDirection: 'row',
      gap: 5,
      '& > span': {
        marginLeft: 6,
      },
    },
    listItemTextSecondary: {
      fontSize: 12,
      marginTop: 8,
    },
    startOfConversation: {
      textAlign: 'center',
      fontSize: '1rem',
      color: '#616c75',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-evenly',
      '& > hr': {
        flex: 1,
      },
    },
    badgeAvatarIcon: {
      width: 17,
      height: 17,
    },
    conversationContainerHeader: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px',
    },
    selectedAgentInfoBlock: {
      display: 'flex',
      flexDirection: 'row',
      gap: 5,
    },
    agentTitle: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      fontWeight: '700',
      lineHeight: '16px',
      textAlign: 'left',
      textUnderlinePosition: 'from-font',
      textDecorationSkipInk: 'none',
      color: '#333333',
    },
    agentSubTitle: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '16px',
      textAlign: 'left',
      textUnderlinePosition: 'from-font',
      textDecorationSkipInk: 'none',
      color: '#333333',
      textTransform: 'capitalize',
    },
    timeStampTxt: {
      fontFamily: 'Open Sans',
      fontSize: '10px',
      fontWeight: '400',
      lineHeight: '13px',
      textAlign: 'left',
      textUnderlinePosition: 'from-font',
      textDecorationSkipInk: 'none',
      color: '#333333',
      marginTop: '4px',
    },
    chatTxt: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '15px',
      textAlign: 'left',
      textUnderlinePosition: 'from-font',
      textDecorationSkipInk: 'none',
      color: '#333333',
    },
    chatDate: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '15px',
      textAlign: 'left',
      textUnderlinePosition: 'from-font',
      textDecorationSkipInk: 'none',
      color: '#767676',
      margin: '0px 15px',
    },
  })
);

/**
 * Component for conversation container
 * @example - <ConversationsContainer />
 * @returns
 */
export const ConversationsAppContainer = () => {
  const styles = useStyles();
  const QuickMessageContainerRef: any = useRef();
  const { accounts } = useMsal();
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const selectedAgent = useSelector(getSelectedAgent);
  const selectedAgentType = useSelector(getSelectedAgentType);

  /**
   * Function to get selected agent messages
   * @param e - any
   * @example getSelectedAgentMessages()
   */
  const getSelectedAgentMessages = () => {
    return selectedAgent?.messages?.map((message: any) => {
      const isDirectOrFavourite =
        selectedAgentType === 'favourite' || selectedAgentType === 'direct';
      const isGroup = selectedAgentType === 'group';
      const isAnotherAgent = message.name === 'another agent';

      return {
        name:
          isDirectOrFavourite && isAnotherAgent
            ? selectedAgent.name
            : isGroup
            ? message.name
            : loginCredentialsFromSSO,
        status:
          isDirectOrFavourite && isAnotherAgent
            ? selectedAgent.status
            : isGroup
            ? message.status
            : 'available',
        text: message.text,
        timestamp: message.timestamp,
        color: message.color,
      };
    });
  };

  const selectedAgentMessages = getSelectedAgentMessages();

  useEffect(() => {
    setTimeout(() => {
      document.getElementById('messageBodyContainer')?.scroll({
        top: document.getElementById('messageBodyContainer')?.scrollHeight, //scroll to the bottom of the element
        behavior: 'smooth', //auto, smooth, initial, inherit
      });
    }, 500);
  }, [selectedAgent]);

  /**
   * Function to render status icon
   * @param e - any
   * @example renderStatusIcon(status)
   */
  function renderStatusIcon(status: string) {
    switch (status) {
      case 'working':
        return <CcfWorkingIcon className={styles.badgeAvatarIcon} />;
      case 'available':
        return <CcfAvailableIcon className={styles.badgeAvatarIcon} />;
      case 'busy':
        return <CcfUnavailableIcon className={styles.badgeAvatarIcon} />;
      case 'offline':
        return (
          <CcfOfflineIcon
            className={styles.badgeAvatarIcon}
            style={{ color: 'inherit' }}
          />
        );
      default:
        return <CcfAvailableIcon className={styles.badgeAvatarIcon} />;
    }
  }

  return (
    <>
      <CcfBox className={styles.conversationContainerHeader}>
        {selectedAgent &&
          Object.entries(selectedAgent).length > 0 &&
          (selectedAgentType === 'group' ? (
            <div className={styles.selectedAgentInfoBlock}>
              <CcfMessageGroupIcon />
              <CcfTypography className={styles.agentTitle}>
                {selectedAgent.name}
              </CcfTypography>
              <CcfTypography className={styles.agentSubTitle}>
                | {selectedAgent?.groupMembers.length} Members
              </CcfTypography>
            </div>
          ) : (
            <div className={styles.selectedAgentInfoBlock}>
              {renderStatusIcon(selectedAgent.status)}
              <CcfTypography className={styles.agentTitle}>
                {selectedAgent.name}
              </CcfTypography>
              <CcfTypography className={styles.agentSubTitle}>
                | {selectedAgent.status}
              </CcfTypography>
            </div>
          ))}
        <CcfOverflowIcon />
      </CcfBox>
      <Divider />
      <div
        className={styles.mainContainer}
        id="messageBodyContainer"
        ref={QuickMessageContainerRef}
      >
        <List>
          {selectedAgentMessages?.map(
            (
              chat: {
                name: string;
                status: string;
                text: string;
                timestamp: string;
                color: string;
              },
              index: number
            ) => (
              <React.Fragment key={`chat-message-${index}`}>
                {(index === 0 ||
                  new Date(chat.timestamp).toLocaleDateString() !==
                    new Date(
                      selectedAgentMessages[index - 1].timestamp
                    ).toLocaleDateString()) && (
                  <div className={styles.startOfConversation}>
                    <Divider />
                    <span className={styles.chatDate}>
                      {new Date(chat.timestamp).toLocaleDateString() ===
                      new Date().toLocaleDateString()
                        ? 'Today'
                        : new Date(chat.timestamp).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'short',
                            day: 'numeric',
                          })}
                    </span>
                    <Divider />
                  </div>
                )}
                <ListItem tabIndex={0} classes={{ root: styles.listItemRoot }}>
                  <ListItemIcon classes={{ root: styles.listItemIcon }}>
                    <CcfBadge
                      overlap="circular"
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      badgeContent={renderStatusIcon(chat.status)}
                    >
                      <CcfAvatar
                        width={25}
                        height={25}
                        fontSize={12}
                        bgcolor={chat.color}
                        borderColor={chat.color}
                        border={true}
                      >
                        {chat.name
                          .split(' ')
                          .map((n: string) => n[0])
                          .join('')}
                      </CcfAvatar>
                    </CcfBadge>
                  </ListItemIcon>
                  <ListItemText
                    classes={{
                      primary: styles.listItemTextPrimary,
                      secondary: styles.listItemTextSecondary,
                    }}
                    primary={
                      <>
                        <CcfTypography className={styles.agentTitle}>
                          {chat.name}
                        </CcfTypography>
                        <CcfTypography className={styles.timeStampTxt}>
                          {new Date(chat.timestamp).toLocaleString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </CcfTypography>
                      </>
                    }
                    secondary={
                      <CcfTypography className={styles.chatTxt}>
                        {chat.text}
                      </CcfTypography>
                    }
                  ></ListItemText>
                </ListItem>
              </React.Fragment>
            )
          )}
        </List>
      </div>
    </>
  );
};
